import { Save, Loader2, Check } from 'lucide-react'
import { Button, SegmentedControl } from '@teach/ui'
import type { Instruction } from '@/features/instructions/types'
import type { ArticleForm } from '@/features/instructions/lib/articleDraft'
import type { TranslationStatus } from './useTranslation'
import { TranslateButton } from './TranslateButton'
import { useMessages, useUiLang } from '@/i18n'
import { messages } from './messages'

// Первым — язык интерфейса: при английском интерфейсе EN слева, RU справа.
const LANGS_RU_FIRST = ['ru', 'en'] as const
const LANGS_EN_FIRST = ['en', 'ru'] as const

interface Props {
  activeLang: 'ru' | 'en'
  onLangChange: (lang: 'ru' | 'en') => void
  dirty: boolean
  saving: boolean
  onSave: () => void
  instruction: Instruction | null
  onTranslate: () => void
  isTranslating: boolean
  translationStatus: TranslationStatus
  /** Статья на проверке: «Сохранить» и «Перевести» погашены. «Обновить» — во вкладке «Доступ». */
  frozen: boolean
  /** Засев формы — от него «Перевести» судит, есть ли перевод. */
  seed: ArticleForm
}

export const EditorToolbar = ({
  activeLang,
  onLangChange,
  dirty,
  saving,
  onSave,
  instruction,
  onTranslate,
  isTranslating,
  translationStatus,
  frozen,
  seed,
}: Props) => {
  const m = useMessages(messages)
  const langs = useUiLang() === 'en' ? LANGS_EN_FIRST : LANGS_RU_FIRST
  return (
    <div className="flex shrink-0 items-center gap-2">
      <SegmentedControl
        size="sm"
        value={activeLang}
        onChange={onLangChange}
        options={langs.map((lang) => ({ id: lang, label: lang.toUpperCase() }))}
      />

      <TranslateButton
        instruction={instruction}
        onTranslate={onTranslate}
        isTranslating={isTranslating}
        translationStatus={translationStatus}
        frozen={frozen}
        seed={seed}
      />

      {/* Нечего сохранять — серая кнопка: фиолетовая выглядела активной. */}
      <Button
        size="sm"
        variant={dirty || saving ? 'default' : 'secondary'}
        onClick={onSave}
        disabled={!dirty || saving || frozen}
        className="h-8 px-2.5 text-xs [&_svg]:size-3.5"
      >
        {saving ? <Loader2 className="animate-spin" /> : dirty ? <Save /> : <Check />}
        {dirty || saving ? m.save : m.saved}
      </Button>
    </div>
  )
}
